import { useCallback, useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Typography } from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  formatDateTime,
  useFleetOperator,
  usePortalPermissions,
  ORGANIZATION_CUSTOMER_TYPES,
  type Organization,
  type OrganizationCustomerType,
} from '@ubus/shared';
import EntityDrawer from '../components/EntityDrawer';
import AdminListPage from '../components/AdminListPage';
import { TableRowActions } from '../components/TableRowActions';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import { localeConfirm } from '../utils/localeModal';
import { formatAdminDbError } from '../utils/formatAdminDbError';

const { Text } = Typography;

/** Fleet owner: customers preferred to this fleet (CONF-20260722-owner-orgs). */
export default function OwnerOrganizations() {
  const { t, i18n } = useTranslation();
  const { operatorId, loading: operatorLoading } = useFleetOperator();
  const { hasPermission } = usePortalPermissions();
  const canCreate = hasPermission('owner_organizations.create');
  const canEdit = hasPermission('owner_organizations.edit');
  const canDelete = hasPermission('owner_organizations.delete');

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [viewOrg, setViewOrg] = useState<Organization | null>(null);
  const [form] = Form.useForm();
  const { pagination, onTableChange, rowClassName, afterMutation } = useAdminTableStability();

  const fetchOrgs = useCallback(async () => {
    if (!operatorId) {
      setOrgs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('preferred_fleet_operator_id', operatorId)
        .order('name', { ascending: true });
      if (error) {
        message.error(error.message);
      } else {
        setOrgs(data ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, [operatorId]);

  useEffect(() => {
    if (!operatorLoading) void fetchOrgs();
  }, [fetchOrgs, operatorLoading]);

  const handleCreate = () => {
    setEditingOrg(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Organization) => {
    setEditingOrg(record);
    form.setFieldsValue({
      ...record,
      notification_emails: (record.notification_emails || []).join('; '),
    });
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    localeConfirm(t, {
      title: t('ownerOrganizations.deleteTitle'),
      content: t('ownerOrganizations.deleteContent'),
      okText: t('ownerOrganizations.delete'),
      cancelText: t('common.cancel'),
      okType: 'danger',
      onOk: async () => {
        const { error } = await supabase.from('organizations').delete().eq('id', id);
        if (error) {
          message.error(formatAdminDbError(error, i18n.language, 'delete'));
        } else {
          message.success(t('ownerOrganizations.deleted'));
          await afterMutation(id, fetchOrgs);
        }
      },
    });
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    if (!operatorId) {
      message.error(t('ownerOrganizations.noOperator'));
      return;
    }
    try {
      const orgData = {
        name: String(values.name ?? '').trim(),
        contact_person: String(values.contact_person ?? '').trim(),
        contact_phone: String(values.contact_phone ?? '').trim(),
        contact_email: String(values.contact_email ?? '').trim(),
        customer_type:
          values.customer_type && String(values.customer_type).trim()
            ? (String(values.customer_type) as OrganizationCustomerType)
            : null,
        address: values.address ? String(values.address) : null,
        requirements: values.requirements ? String(values.requirements) : null,
        notification_emails: values.notification_emails
          ? String(values.notification_emails)
              .split(/[;,]/)
              .map((e: string) => e.trim())
              .filter(Boolean)
          : [],
        preferred_fleet_operator_id: operatorId,
      };

      if (editingOrg) {
        const { error } = await supabase
          .from('organizations')
          .update(orgData)
          .eq('id', editingOrg.id);
        if (error) {
          message.error(error.message);
          return;
        }
        message.success(t('ownerOrganizations.updated'));
        setModalVisible(false);
        await afterMutation(editingOrg.id, fetchOrgs);
      } else {
        const { data: created, error } = await supabase
          .from('organizations')
          .insert([orgData])
          .select('id')
          .single();
        if (error) {
          message.error(error.message);
          return;
        }
        message.success(t('ownerOrganizations.created'));
        setModalVisible(false);
        await afterMutation(created?.id, fetchOrgs);
      }
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('ownerOrganizations.saveFailed'));
    }
  };

  const columns: TableProps<Organization>['columns'] = [
    {
      title: t('ownerOrganizations.columns.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: 'ascend',
    },
    {
      title: t('ownerOrganizations.columns.customerType'),
      dataIndex: 'customer_type',
      key: 'customer_type',
      width: 120,
      render: (v: OrganizationCustomerType | null | undefined) =>
        v ? t(`ownerOrganizations.customerTypes.${v}`) : '—',
    },
    {
      title: t('ownerOrganizations.columns.contactPerson'),
      dataIndex: 'contact_person',
      key: 'contact_person',
      width: 140,
    },
    {
      title: t('ownerOrganizations.columns.phone'),
      dataIndex: 'contact_phone',
      key: 'contact_phone',
      width: 140,
    },
    {
      title: t('ownerOrganizations.columns.email'),
      dataIndex: 'contact_email',
      key: 'contact_email',
      width: 220,
      ellipsis: true,
    },
    {
      title: t('ownerOrganizations.columns.address'),
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      render: (v: string | null | undefined) => v || '—',
    },
    {
      title: t('ownerOrganizations.columns.actions'),
      key: 'actions',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <TableRowActions>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewOrg(record)}>
            {t('ownerOrganizations.view')}
          </Button>
          {canEdit ? (
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              {t('ownerOrganizations.edit')}
            </Button>
          ) : null}
          {canDelete ? (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            >
              {t('ownerOrganizations.delete')}
            </Button>
          ) : null}
        </TableRowActions>
      ),
    },
  ];

  return (
    <>
      <AdminListPage
        title={t('ownerOrganizations.title')}
        subtitle={t('ownerOrganizations.subtitle')}
        extra={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} disabled={!operatorId}>
              {t('ownerOrganizations.add')}
            </Button>
          ) : undefined
        }
        resultCount={orgs.length}
      >
        <Table
          dataSource={orgs}
          columns={columns}
          loading={loading || operatorLoading}
          rowKey="id"
          pagination={pagination}
          onChange={onTableChange}
          rowClassName={rowClassName}
          scroll={{ x: 1000 }}
          locale={{ emptyText: t('ownerOrganizations.empty') }}
        />
      </AdminListPage>

      <Modal
        title={editingOrg ? t('ownerOrganizations.edit') : t('ownerOrganizations.add')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText={t('ownerOrganizations.save')}
        cancelText={t('common.cancel')}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label={t('ownerOrganizations.form.name')}
            rules={[{ required: true, message: t('ownerOrganizations.form.nameRequired') }]}
          >
            <Input placeholder={t('ownerOrganizations.form.namePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="customer_type"
            label={t('ownerOrganizations.form.customerType')}
            extra={t('ownerOrganizations.form.customerTypeHelp')}
          >
            <Select
              allowClear
              placeholder={t('ownerOrganizations.form.customerTypePlaceholder')}
              options={ORGANIZATION_CUSTOMER_TYPES.map((value) => ({
                value,
                label: t(`ownerOrganizations.customerTypes.${value}`),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="contact_person"
            label={t('ownerOrganizations.form.contactPerson')}
            rules={[{ required: true, message: t('ownerOrganizations.form.contactPersonRequired') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="contact_phone"
            label={t('ownerOrganizations.form.contactPhone')}
            rules={[{ required: true, message: t('ownerOrganizations.form.contactPhoneRequired') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="contact_email"
            label={t('ownerOrganizations.form.contactEmail')}
            rules={[
              { required: true, message: t('ownerOrganizations.form.contactEmailRequired') },
              { type: 'email', message: t('ownerOrganizations.form.contactEmailInvalid') },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="notification_emails" label={t('ownerOrganizations.form.notificationEmails')}>
            <Input placeholder={t('ownerOrganizations.form.notificationEmailsPlaceholder')} />
          </Form.Item>
          <Form.Item name="address" label={t('ownerOrganizations.form.address')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="requirements" label={t('ownerOrganizations.form.requirements')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('ownerOrganizations.form.fleetBoundHint')}
          </Text>
        </Form>
      </Modal>

      <EntityDrawer
        open={!!viewOrg}
        onClose={() => setViewOrg(null)}
        title={viewOrg?.name || t('ownerOrganizations.detailsTitle')}
        width={640}
      >
        {viewOrg ? (
          <Table
            size="small"
            pagination={false}
            showHeader={false}
            dataSource={[
              { key: 'name', label: t('ownerOrganizations.columns.name'), value: viewOrg.name },
              {
                key: 'customerType',
                label: t('ownerOrganizations.columns.customerType'),
                value: viewOrg.customer_type
                  ? t(`ownerOrganizations.customerTypes.${viewOrg.customer_type}`)
                  : '—',
              },
              {
                key: 'contact',
                label: t('ownerOrganizations.columns.contactPerson'),
                value: viewOrg.contact_person,
              },
              { key: 'phone', label: t('ownerOrganizations.columns.phone'), value: viewOrg.contact_phone },
              { key: 'email', label: t('ownerOrganizations.columns.email'), value: viewOrg.contact_email },
              {
                key: 'address',
                label: t('ownerOrganizations.columns.address'),
                value: viewOrg.address || '—',
              },
              {
                key: 'created',
                label: t('ownerOrganizations.columns.created'),
                value: formatDateTime(viewOrg.created_at),
              },
            ]}
            columns={[
              {
                dataIndex: 'label',
                key: 'label',
                width: 140,
                render: (v: string) => <Text strong>{v}</Text>,
              },
              { dataIndex: 'value', key: 'value' },
            ]}
          />
        ) : null}
      </EntityDrawer>
    </>
  );
}
